import React from "react";
import { Loader } from "smart-react";
import { buildNotification, GenerateNotification } from "smart-react";
import {
  listProducts,
  subscribeProducts,
  subscribeAllFeatures,
} from "../../Products/Services/ProductService";
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from "../../../constants/eventDataTypes";
import {
  SUBSCRIPTION_ERROR_MESSAGE,
  SUBSCRIPTION_SUCCESS_MESSAGE,
} from "../../../constants/notificationMessages";
import { USER_PRODUCT_FEATURES } from "../../../constants/applicationConstants";
import {
  isAllProductFeaturesSubscribed,
  isAllProductFeaturesExpired,
  isAnyProductFeatureExpired,
  isOrganizationAdminRole,
  isWorldTenant,
  hasFeaturePermissions,
  hasAnyFeaturePermissions,
} from "../../../Utils/Auth/ValidatePermissions";
import { encryptAndStore } from "../../../Utils/Storage/localStorageUtil";
import { useAuth } from "../../../Modules/Core/Context/AuthContext";

/**
 * Product List HOC
 * @WrappedComponent {React.Component}
 * @returns {React.ReactElement}
 */
const ProductListHOC = (WrappedComponent) => {
  return function WithProductManagement(props) {
    const { tenantID, isOrganizationProduct } = props;
    const [products, setProducts] = React.useState([]);
    const [loader, setIsLoader] = React.useState(true);
    const [expanded, setExpanded] = React.useState("");
    const [visibleDialog, setVisibleDialog] = React.useState(false);
    const [aggrement, setAgreement] = React.useState(false);
    const [isError, setIsError] = React.useState(false);
    const [selectedFeature, setSelectedFeature] = React.useState(null);
    const [productID, setProductID] = React.useState(null);
    const [isRefesh, setIsRefesh] = React.useState(false);

    const { user, setFeatureData } = useAuth();

    /**
     * Handle Dialog close
     */
    const toggleDialog = () => {
      setProductID(null);
      setSelectedFeature(null);
      setAgreement(false);
      setIsError(false);
      setVisibleDialog((previousState) => !previousState);
    };
    /**
     * Handle the click on subscribe button
     * @param {String} featureId
     * @returns
     */
    const handleSubscribeFeature = (featureId) => {
      toggleDialog();
      setSelectedFeature(featureId);
    };
    /**
     * Handle the click on subscribe all button
     * @param {String} productId
     * @returns
     */
    const handleSubscribeAll = (productId) => {
      toggleDialog();
      setProductID(productId);
    };
    /**
     * Handle agreement checkbox click
     * @param {Object} event
     * @returns
     */
    const handleAgreementChange = (eventOrValue) => {
      if (typeof eventOrValue === "boolean") {
        setIsError(false);
        setAgreement(eventOrValue);
        return;
      }

      setIsError(false);
      setAgreement(
        Boolean(eventOrValue?.value ?? eventOrValue?.target?.checked),
      );
    };

    /**
     * Handle Subscribe Form Submission
     */
    const submitDialog = async () => {
      if (aggrement) {
        setIsError(false);
        setIsLoader(true);
        if (productID === null) {
          const susbcribeProducts = await subscribeProducts({
            feature_id: selectedFeature,
            user,
            tenantId: tenantID,
          });
          if (susbcribeProducts?.IsSuccess) {
            GenerateNotification(
              buildNotification(SUBSCRIPTION_SUCCESS_MESSAGE),
              NOTIFICATION_TYPES.APP,
              EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
            );
            setIsRefesh(!isRefesh);
          } else {
            GenerateNotification(
              buildNotification(SUBSCRIPTION_ERROR_MESSAGE),
              NOTIFICATION_TYPES.APP,
              EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
            );
          }
        }
        if (selectedFeature === null) {
          const susbcribeProducts = await subscribeAllFeatures({
            product_id: productID,
            user,
            tenantId: tenantID,
          });
          if (susbcribeProducts?.IsSuccess) {
            GenerateNotification(
              buildNotification(SUBSCRIPTION_SUCCESS_MESSAGE),
              NOTIFICATION_TYPES.APP,
              EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
            );
            setIsRefesh(!isRefesh);
          } else {
            GenerateNotification(
              buildNotification(SUBSCRIPTION_ERROR_MESSAGE),
              NOTIFICATION_TYPES.APP,
              EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
            );
          }
        }
        setProductID(null);
        setSelectedFeature(null);
        toggleDialog();
        setIsLoader(false);
        setAgreement(false);
      } else {
        setIsError(true);
      }
    };
    /**
     * Check all features is enable or not
     */
    const isFeaturesEnabled = (product) => {
      return product?.features.some((feature) => feature?.IsEnabled);
    };
    /**
     * Resfresh the
     */
    const updateData = () => {
      setIsRefesh(!isRefesh);
    };

    /**
     * Used to get list of products
     */
    React.useEffect(() => {
      (async () => {
        setIsLoader(true);
        const productsData = await listProducts({
          tenantId: tenantID,
          type: isOrganizationProduct ? "organization" : "both",
        });
        setProducts(productsData);
        setIsLoader(false);
      })();
    }, [isRefesh, tenantID]);

    /**
     * Used to get list of products
     */
    React.useEffect(() => {
      if (!isOrganizationProduct && products?.length > 0) {
        setFeatureData(products);
        encryptAndStore(`${USER_PRODUCT_FEATURES}`, products, false);
      }
    }, [products]);

    const productManagementProps = {
      products,
      loader,
      expanded,
      setExpanded,
      visibleDialog,
      toggleDialog,
      aggrement,
      handleAgreementChange,
      isError,
      selectedFeature,
      handleSubscribeFeature,
      productID,
      handleSubscribeAll,
      submitDialog,
      isRefesh,
      updateData,
      isFeaturesEnabled,
      isAllProductFeaturesSubscribed,
      isAllProductFeaturesExpired,
      isAnyProductFeatureExpired,
      isOrganizationAdminRole,
      isWorldTenant,
      hasFeaturePermissions,
      hasAnyFeaturePermissions,
      user,
    };

    return (
      <React.Suspense fallback={<Loader />}>
        <WrappedComponent {...props} {...productManagementProps} />
      </React.Suspense>
    );
  };
};

export default ProductListHOC;
